/* GemGlow v3: animated add confirmation, followed by the cart page. */
(() => {
  if (customElements.get('product-form')) return;

  // Keep the animation with this asset so no additional Liquid/CSS edit is needed.
  if (!document.getElementById('gg-atc-redirect-styles')) {
    const style = document.createElement('style');
    style.id = 'gg-atc-redirect-styles';
    style.textContent = `
      product-form button[data-gg-atc-state] { position: relative; cursor: progress; }
      product-form button[data-gg-atc-state] > span:not(.sold-out-message) {
        opacity: 1 !important; visibility: visible !important;
      }
      product-form button[data-gg-atc-state] .loading__spinner { display: none !important; }
      product-form .gg-atc-spinner { display: none; }
      product-form button[data-gg-atc-state="adding"] .gg-atc-spinner {
        display: inline-block; width: 1.6rem; height: 1.6rem; margin-inline-start: 1rem;
        flex: 0 0 1.6rem; box-sizing: border-box; border: 2px solid currentColor;
        border-inline-end-color: transparent; border-radius: 50%;
        animation: gg-atc-spin .65s linear infinite;
      }
      product-form button[data-gg-atc-state="added"] {
        background: #36523a !important; color: #fff !important;
        animation: gg-atc-confirm .35s ease-out;
      }
      product-form .gg-atc-status {
        margin: .8rem 0 0; font: inherit; font-size: 1.3rem; line-height: 1.5;
      }
      product-form .gg-atc-status:empty { display: none; }
      @keyframes gg-atc-spin { to { transform: rotate(360deg); } }
      @keyframes gg-atc-confirm {
        0%, 100% { transform: scale(1); }
        45% { transform: scale(1.025); }
      }
      @media (prefers-reduced-motion: reduce) {
        product-form button[data-gg-atc-state], product-form .gg-atc-spinner {
          animation: none !important; transition: none !important;
        }
      }
    `;
    document.head.append(style);
  }

  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.onSubmitHandler.bind(this);
      this.onPageShow = (event) => {
        if (event.persisted && this.redirecting) this.resetButton();
      };
      this.buttonRevision = 0;
      this.init();
    }

    connectedCallback() {
      this.init();
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() { window.removeEventListener('pageshow', this.onPageShow); }

    init() {
      if (this.initialized) return;
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      if (!this.form || !this.submitButton) return;
      this.initialized = true;
      this.submitButtonText = this.submitButton.querySelector('span:not(.loading__spinner):not(.sold-out-message)');
      if (!this.submitButtonText) {
        this.submitButtonText = document.createElement('span');
        const textNodes = [...this.submitButton.childNodes].filter((node) => node.nodeType === 3);
        this.submitButtonText.textContent = textNodes.map((node) => node.textContent).join('').trim() || 'Add to cart';
        textNodes.forEach((node) => node.remove());
        this.submitButton.prepend(this.submitButtonText);
      }
      this.spinner = this.querySelector('.loading__spinner');
      const feedbackSpinner = document.createElement('span');
      feedbackSpinner.className = 'gg-atc-spinner';
      feedbackSpinner.setAttribute('aria-hidden', 'true');
      this.submitButton.append(feedbackSpinner);
      this.hideErrors = this.dataset.hideErrors === 'true';
      if (this.variantIdInput) this.variantIdInput.disabled = false;
      this.form.addEventListener('submit', this.onSubmit);
      this.submitButton.removeAttribute('aria-haspopup');
      this.status = document.createElement('div');
      this.status.className = 'gg-atc-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      this.append(this.status);
    }

    get variantIdInput() { return this.form?.querySelector('[name="id"]'); }
    get cartURL() { return window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`; }
    navigateToCart() { window.location.assign(this.cartURL); }

    resetButton() {
      this.submitting = false;
      this.redirecting = false;
      this.submitButton.classList.remove('loading', 'gg-cart-loading', 'gg-cart-added');
      this.submitButton.removeAttribute('data-gg-atc-state');
      this.submitButton.removeAttribute('aria-busy');
      this.spinner?.classList.add('hidden');
      if (!this.submitButton.disabled) this.submitButton.removeAttribute('aria-disabled');
      if (this.buttonRevision === this.submissionRevision && !this.submitButton.disabled) {
        this.submitButtonText.textContent = this.normalButtonText || window.variantStrings?.addToCart || 'Add to cart';
      }
      this.status.textContent = '';
    }

    async confirmAndRedirect() {
      this.redirecting = true;
      this.submitButton.classList.remove('loading', 'gg-cart-loading');
      this.submitButton.classList.add('gg-cart-added');
      this.submitButton.setAttribute('data-gg-atc-state', 'added');
      this.submitButton.removeAttribute('aria-busy');
      this.spinner?.classList.add('hidden');
      if (this.buttonRevision === this.submissionRevision && !this.submitButton.disabled) {
        this.submitButtonText.textContent = this.dataset.addedText || 'Added ✓';
      }
      this.status.textContent = 'Added to cart. Opening your cart…';
      const cartLink = document.createElement('a');
      cartLink.href = this.cartURL;
      cartLink.className = 'link';
      cartLink.textContent = ' View cart';
      this.status.append(cartLink);
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      await new Promise((resolve) => setTimeout(resolve, reducedMotion ? 150 : 650));
      this.navigateToCart();
    }

    publishSafely(eventName, payload) {
      if (!eventName || typeof publish !== 'function') return;
      try { Promise.resolve(publish(eventName, payload)).catch(console.warn); }
      catch (error) { console.warn(error); }
    }

    async onSubmitHandler(evt) {
      // Respect a previously executed custom submit handler (for example, a service bundle).
      if (evt.defaultPrevented) return;
      evt.preventDefault();
      if (this.submitting || this.redirecting || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.submitting = true;
      this.error = false;
      this.submissionRevision = this.buttonRevision;
      const originalText = this.normalButtonText || this.submitButtonText?.textContent || window.variantStrings?.addToCart || 'Add to cart';
      this.normalButtonText = originalText;
      let added = false;
      let deferred;
      let variantId;
      this.handleErrorMessage();
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.classList.remove('gg-cart-added');
      this.submitButton.classList.add('loading', 'gg-cart-loading');
      this.submitButton.setAttribute('data-gg-atc-state', 'adding');
      this.spinner?.classList.add('hidden');
      if (this.submitButtonText) this.submitButtonText.textContent = this.dataset.addingText || 'Adding…';
      this.status.textContent = this.dataset.addingText || 'Adding…';

      try {
        const formData = new FormData(this.form);
        variantId = formData.get('id');
        const quantity = Number.parseInt(formData.get('quantity'), 10) || 1;
        // The destination page renders the cart; no drawer fragments are needed.
        formData.delete('sections');
        formData.delete('sections_url');
        deferred = this.createCartLinesUpdateEvent(variantId, quantity);
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = typeof data.description === 'string' ? data.description : (typeof data.message === 'string' ? data.message : 'This item could not be added. Please check the selected quantity.');
          this.error = true;
          this.handleErrorMessage(message);
          this.status.textContent = message;
          this.publishSafely(typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartError : null, {
            source: 'product-form', productVariantId: variantId, errors: data.errors || message, message,
          });
          this.dispatchCartErrorEvent(message, 'INVALID');
          deferred?.reject(new Error(message));
          return;
        }

        added = true;
        this.resolveCartLinesUpdate(deferred);
        await this.confirmAndRedirect();
      } catch (error) {
        console.warn('GemGlow cart:', error);
        if (added) {
          // The add already succeeded. Do not retry the POST or claim the product failed.
          this.redirecting = true;
          this.handleErrorMessage('Added to cart. Use View cart to continue.', true);
        } else {
          this.error = true;
          const message = 'We could not confirm this addition. Check your cart before trying again.';
          this.handleErrorMessage(message, true);
          this.status.textContent = message;
          this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
          deferred?.reject(error);
        }
      } finally {
        // Keep repeat clicks blocked until navigation or browser Back restores the page.
        if (!added) {
          const message = this.status.textContent;
          this.resetButton();
          this.status.textContent = message;
        }
      }
    }

    handleErrorMessage(errorMessage = false, showCartLink = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper && errorMessage) {
        this.errorMessageWrapper = document.createElement('div');
        this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
        this.errorMessageWrapper.setAttribute('role', 'alert');
        this.prepend(this.errorMessageWrapper);
      }
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessageWrapper.querySelector('.product-form__error-message');
      if (!this.errorMessage) {
        this.errorMessage = document.createElement('span');
        this.errorMessage.className = 'product-form__error-message';
        this.errorMessageWrapper.append(this.errorMessage);
      }
      this.errorMessageWrapper.querySelector('[data-gg-error-cart-link]')?.remove();
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      this.errorMessage.textContent = errorMessage || '';
      if (errorMessage && showCartLink) {
        const link = document.createElement('a');
        link.href = this.cartURL;
        link.textContent = ' View cart';
        link.className = 'link';
        link.setAttribute('data-gg-error-cart-link', '');
        this.errorMessageWrapper.append(link);
      }
    }

    toggleSubmitButton(disable = true, text) {
      this.buttonRevision += 1;
      this.submitButton.classList.remove('gg-cart-added');
      this.submitButton.toggleAttribute('disabled', disable);
      if (!disable) this.submitButton.removeAttribute('aria-disabled');
      const label = text || (!disable ? window.variantStrings?.addToCart || 'Add to cart' : null);
      if (label && this.submitButtonText) this.submitButtonText.textContent = label;
      this.normalButtonText = !disable ? label : null;
    }

    createCartLinesUpdateEvent(variantId, quantity) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      try {
        const deferred = CartLinesUpdateEvent.createPromise();
        deferred.promise.catch(() => {});
        this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: variantId, quantity }], promise: deferred.promise }));
        return deferred;
      } catch (error) { console.warn(error); return null; }
    }

    resolveCartLinesUpdate(deferred) {
      if (!deferred) return;
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      try {
        const cartPromise = typeof CartItems !== 'undefined' && typeof CartItems.fetchCartData === 'function'
          ? CartItems.fetchCartData()
          : fetch(`${this.cartURL}.js`).then((response) => response.json());
        Promise.resolve(cartPromise).then((cart) => {
          if (!cart?.currency) throw new Error('Missing currency in cart response');
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        }).catch((error) => deferred.reject(error));
      } catch (error) { deferred.reject(error); }
    }

    dispatchCartErrorEvent(message, code) {
      const { CartErrorEvent } = window.StandardEvents || {};
      if (!CartErrorEvent) return;
      try { this.dispatchEvent(new CartErrorEvent({ error: message, code })); }
      catch (error) { console.warn(error); }
    }
  });
})();
